import React, { useMemo } from "react";
import { Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetCRAByCollaboratorQuery } from "store/cra/cra.api";
import { useGetProjectsByCollaboratorQuery } from "store/project/project.api";

import "./CraListeMonthPage.scss";
import { useTranslation } from "react-i18next";

// Fonction utilitaire pour générer tous les mois entre deux dates
function generateMonthsList(startDate) {
  const start = new Date(startDate);
  const end = new Date();
  const months = [];
  let current = new Date(start.getFullYear(), start.getMonth(), 1);

  while (current <= end) {
    const monthName = current.toLocaleString("fr-FR", { month: "long" });
    const year = current.getFullYear();
    months.push({
      month: current.getMonth() + 1,
      year,
      period: `${monthName} ${year}`,
      status: "pending",
    });
    current.setMonth(current.getMonth() + 1);
  }

  return months.reverse();
}

function TimesheetsSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const { data: projectsData, isLoading: projectsLoading } =
    useGetProjectsByCollaboratorQuery(currentUser._id);
  const { data: craData, isLoading: craLoading } = useGetCRAByCollaboratorQuery(
    currentUser._id
  );

  const projects = projectsData?.data?.projects || [];
  const cras = craData?.data || [];

  const firstStartDate = projects.length
    ? projects.reduce((min, p) => {
        const start = new Date(p.startDate);
        return start < min ? start : min;
      }, new Date())
    : new Date();

  const monthsList = useMemo(
    () => generateMonthsList(firstStartDate),
    [firstStartDate]
  );

  const timesheets = monthsList.map(({ month, year, period }) => {
    const cra = cras.find((c) => c.month === month && c.year === year);

    if (cra) {
      let displayStatus = "";
      switch (cra.status) {
        case "draft":
          displayStatus = t("cra.status_draft");
          break;
        case "pending_validation":
          displayStatus = t("cra.status_pending_validation");
          break;
        case "validated":
          displayStatus = t("cra.status_validated");
          break;
        case "refused":
          displayStatus = t("cra.status_refused");
          break;
        default:
          displayStatus = t("cra.status_pending");
      }
      return { ...cra, period, displayStatus };
    } else {
      return {
        month,
        year,
        period,
        status: "pending",
        displayStatus: t("timesheet.status_pending"),
      };
    }
  });

  const handleRowClick = (ts) => {
    if (ts.status === "pending") {
      navigate(`/cra-details/${encodeURIComponent(ts.period)}`);
    } else {
      switch (ts.status) {
        case "draft":
        case "refused":
          navigate(`/cra/${ts._id}`);
          break;
        case "pending_validation":
        case "validated":
          navigate(`/craConsult/${ts._id}`);
          break;
        default:
          navigate(`/cra-details/${encodeURIComponent(ts.period)}`);
      }
    }
  };

  if (projectsLoading || craLoading) return <div>{t("timesheet.loading")}</div>;

  return (
    <div className="timesheets-card">
      <h6 className="title">
        {t("timesheet.timesheets_title")}{" "}
        <span className="count">({timesheets.length})</span>
      </h6>
      <Table borderless responsive className="timesheets-table">
        <thead>
          <tr>
            <th>{t("timesheet.period")}</th>
            <th className="text-end">{t("timesheet.state")}</th>
          </tr>
        </thead>
        <tbody>
          {timesheets.map((ts, idx) => (
            <tr
              key={idx}
              style={{ cursor: "pointer" }}
              onClick={() => handleRowClick(ts)}>
              <td>{ts.period}</td>
              <td className="text-end">{ts.displayStatus}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export default TimesheetsSection;
